import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { API, apiError, formatINR, formatDate, daysUntil } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Search, Edit2, Trash2, Package, Upload, BookPlus } from "lucide-react";
import { toast } from "sonner";
import BulkSelectBar from "../components/BulkSelectBar";

export default function Stock() {
  const { user } = useAuth();
  const canBulkDelete = user?.role === "admin" || user?.role === "branch_head";
  const isAdmin = user?.role === "admin";
  const [meds, setMeds] = useState([]);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/medicines", { params: { q: q || undefined, limit: 500 } });
      setMeds(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    API.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const branchName = (bid) => branches.find((b) => b.id === bid)?.name || "—";
  const showBranchCol = isAdmin;

  const del = async (m) => {
    if (!window.confirm(`Delete ${m.name}? This cannot be undone.`)) return;
    try {
      await API.delete(`/medicines/${m.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const seedCatalog = async () => {
    if (!window.confirm("Import the standard 25-medicine starter catalogue into your branch? Existing items with the same name are skipped. Each placeholder is added with qty=0 — edit batch / dates / quantity afterwards.")) return;
    setSeeding(true);
    try {
      const { data } = await API.post("/medicines/seed-catalog");
      toast.success(`Catalogue imported · ${data.inserted} added, ${data.skipped} skipped`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSeeding(false); }
  };

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected medicines? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await API.post("/bulk-delete", { entity: "medicines", ids: [...selected] });
      toast.success(`${data.deleted} deleted${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkBusy(false); }
  };

  const filtered = meds.filter((m) => {
    if (filter === "low") return m.quantity <= (m.low_stock_threshold || 10);
    if (filter === "expiring") { const d = daysUntil(m.expiry_date); return d !== null && d <= 90; }
    if (filter === "out") return m.quantity === 0;
    return true;
  });

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="stock-page">
      {!isAdmin && user?.branch_id && branches.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-[#1E40AF] bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 w-fit" data-testid="stock-branch-scope">
          <Package className="w-3.5 h-3.5" />
          <span>Showing inventory for <b>{branchName(user.branch_id)}</b> branch only</span>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Inventory</div>
          <h1 className="font-display text-3xl font-bold">Stock Management</h1>
          <p className="text-sm text-slate-500 mt-1">Track batches, expiry and rack locations across your branch.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={seedCatalog} disabled={seeding} className="btn-ghost" data-testid="seed-catalog-btn" title="Import the standard 25-medicine catalogue as placeholders">
            <BookPlus className="w-4 h-4" /> {seeding ? "Importing…" : "Import Catalogue"}
          </button>
          <Link to="/stock/bulk-import" className="btn-ghost" data-testid="bulk-import-btn">
            <Upload className="w-4 h-4" /> Bulk Import
          </Link>
          <Link to="/stock/new" className="btn-primary" data-testid="add-medicine-btn">
            <Plus className="w-4 h-4" /> Add Medicine
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, company, batch, barcode…"
            data-testid="stock-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
          />
        </div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="stock-filters">
          {[["all", "All"], ["low", "Low"], ["out", "Out of Stock"], ["expiring", "Expiring"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setFilter(v)}
              data-testid={`filter-${v}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === v ? "bg-[#002FA7] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {l}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} items</span>
      </div>

      {canBulkDelete && (
        <BulkSelectBar
          count={selected.size}
          total={filtered.length}
          onClear={() => setSelected(new Set())}
          onSelectAll={() => setSelected(new Set(filtered.map((m) => m.id)))}
          onDelete={bulkDelete}
          busy={bulkBusy}
          entityLabel="medicines"
        />
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500 text-sm">Loading medicines…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-sm text-slate-500">No medicines match your filters.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  {canBulkDelete && (
                    <th className="w-8">
                      <input
                        type="checkbox"
                        data-testid="stock-select-all"
                        checked={filtered.length > 0 && selected.size === filtered.length}
                        onChange={(e) => {
                          if (e.target.checked) setSelected(new Set(filtered.map((m) => m.id)));
                          else setSelected(new Set());
                        }}
                      />
                    </th>
                  )}
                  <th>Medicine</th>{showBranchCol && <th>Branch</th>}<th>Batch</th><th>Expiry</th>
                  <th className="text-right">MRP</th><th className="text-right">Purchase</th>
                  <th className="text-right">Qty</th><th>Location</th><th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const d = daysUntil(m.expiry_date);
                  return (
                    <tr key={m.id} data-testid={`stock-row-${m.id}`} className={selected.has(m.id) ? "bg-blue-50/50" : ""}>
                      {canBulkDelete && (
                        <td>
                          <input
                            type="checkbox"
                            data-testid={`stock-select-${m.id}`}
                            checked={selected.has(m.id)}
                            onChange={() => toggleOne(m.id)}
                          />
                        </td>
                      )}
                      <td>
                        <Link to={`/medicine/${m.id}`} className="font-semibold text-slate-900 hover:text-[#002FA7]">
                          {m.name}
                        </Link>
                        <div className="text-xs text-slate-500">{m.company}</div>
                      </td>
                      {showBranchCol && (
                        <td>
                          <span className="tag tag-info text-[10px]" data-testid={`stock-branch-${m.id}`} title={m.branch_id}>
                            {branchName(m.branch_id)}
                          </span>
                        </td>
                      )}
                      <td><span className="font-mono text-xs">{m.batch_number}</span></td>
                      <td>
                        <div>{formatDate(m.expiry_date)}</div>
                        {d !== null && d <= 90 && (
                          <span className={`tag ${d < 30 ? "tag-danger" : "tag-warning"} mt-1`}>{d}d left</span>
                        )}
                      </td>
                      <td className="text-right font-medium">{formatINR(m.mrp)}</td>
                      <td className="text-right text-slate-500">{formatINR(m.purchase_price)}</td>
                      <td className="text-right">
                        <span className={`tag ${m.quantity === 0 ? "tag-danger" : m.quantity <= (m.low_stock_threshold || 10) ? "tag-warning" : "tag-success"}`}>
                          {m.quantity}
                        </span>
                      </td>
                      <td className="font-mono text-xs">{m.location || "—"}</td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Link to={`/stock/edit/${m.id}`} data-testid={`edit-${m.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7]">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Link>
                          <button type="button" onClick={() => del(m)} data-testid={`delete-${m.id}`} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
